import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'

import { CRM_COMPANY_TYPES, crmService, type CrmCompanyType, type CrmCreateContactInput } from '@/services/crm'
import DialogShell from './DialogShell'

/**
 * Add one contact to the CRM by hand.
 *
 * The "Add Contact" button had no handler at all — it rendered, it hovered,
 * and it did nothing, so the only way a contact reached the directory was
 * through a deal. This is what it opens.
 *
 * Any of the eight types can be entered. A broker becomes a broker master
 * (with its firm) and a sponsor a sponsor master — MASTER records, which is
 * right for a contact an operator typed deliberately: the Q1 invariant asks
 * for operator confirmation before a master exists, and this is that
 * confirmation. Every other type — counsel, an appraiser, an investor —
 * becomes a directory row the operator owns, confirmed on arrival.
 *
 * The server checks for someone already on record at that address or name
 * before filing anything, and answers 409 with the existing id; the dialog
 * offers to open that contact rather than pretending the save went through.
 */
const TYPE_LABELS: Record<CrmCompanyType, string> = {
  Broker: 'Broker',
  'Borrower/Sponsor': 'Borrower / Sponsor',
  Investor: 'Investor',
  'Law Firm': 'Law Firm',
  Appraiser: 'Appraiser',
  Insurance: 'Insurance',
  Internal: 'Internal',
  Unknown: 'Unknown',
}

export default function AddContactDialog({
  onClose,
  onOpenExisting,
}: {
  onClose: () => void
  /** Called with the id of the contact already on record when the server
   *  refuses a duplicate and the person chooses to open it. */
  onOpenExisting?: (id: string) => void
}) {
  const queryClient = useQueryClient()
  const [companyType, setCompanyType] = useState<CrmCompanyType>('Broker')
  const [name, setName] = useState('')
  const [company, setCompany] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [title, setTitle] = useState('')
  const [notes, setNotes] = useState('')
  const [isEntity, setIsEntity] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [existing, setExisting] = useState<{ id: string; detail: string | null } | null>(null)

  // A broker belongs to a firm, so the firm is required for one. A sponsor IS
  // the entity, so its own name is enough; for anyone else the company is
  // simply whatever is known.
  const isBroker = companyType === 'Broker'
  const isSponsor = companyType === 'Borrower/Sponsor'
  const canSave = name.trim().length > 0 && (!isBroker || company.trim().length > 0)

  const save = useMutation({
    mutationFn: () => {
      const body: CrmCreateContactInput = { name: name.trim(), companyType }
      // Only what was typed is sent: an empty string is not "no phone", it is
      // "nothing entered", and the server keeps nulls for those.
      const companyValue = company.trim() || (isSponsor ? name.trim() : '')
      if (companyValue) body.company = companyValue
      if (email.trim()) body.email = email.trim()
      if (phone.trim()) body.phone = phone.trim()
      if (title.trim()) body.title = title.trim()
      if (notes.trim()) body.notes = notes.trim()
      if (isEntity) body.isEntity = true
      return crmService.createContact(body)
    },
    onSuccess: async () => {
      // A saved broker lands in the broker directory and a saved sponsor in
      // the sponsor list; both feed the unified CRM roster. Re-read every
      // view so the new contact shows up wherever the dialog was opened from.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['crm', 'contacts'] }),
        queryClient.invalidateQueries({ queryKey: ['crm', 'lists'] }),
        queryClient.invalidateQueries({ queryKey: ['crm', 'activity'] }),
        queryClient.invalidateQueries({ queryKey: ['broker-firms'] }),
        queryClient.invalidateQueries({ queryKey: ['broker-contacts'] }),
      ])
      onClose()
    },
    onError: (failure) => {
      const response = (failure as { response?: { status?: number; data?: { detail?: unknown; existingId?: unknown } } })
        ?.response
      const detail = typeof response?.data?.detail === 'string' ? response.data.detail : null
      if (response?.status === 409 && typeof response.data?.existingId === 'string') {
        setExisting({ id: response.data.existingId, detail })
        return
      }
      setError(detail ?? 'The contact could not be saved.')
    },
  })

  return (
    <DialogShell label="Add contact" onClose={onClose} width="md">
      <form
        className="min-h-0 flex-1 space-y-3 overflow-y-auto scrollbar-thin px-4 py-3"
        onSubmit={(event) => {
          event.preventDefault()
          setError(null)
          setExisting(null)
          if (canSave) save.mutate()
        }}
      >
        <div>
          <div className="mb-1 block text-[10px] uppercase tracking-[0.12em] text-stone-600">Type</div>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Contact type">
            {CRM_COMPANY_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                aria-pressed={companyType === type}
                onClick={() => setCompanyType(type)}
                className={clsx(
                  'rounded-md border px-2.5 py-1 text-[11px]',
                  companyType === type
                    ? 'border-amber-400/30 bg-amber-400/[0.10] text-amber-200'
                    : 'border-amber-400/10 bg-black/30 text-stone-400 hover:text-stone-200',
                )}
              >
                {TYPE_LABELS[type]}
              </button>
            ))}
          </div>
        </div>

        <Field label="Name" required value={name} onChange={setName} autoFocus />
        <Field
          label={isBroker ? 'Firm' : isSponsor ? 'Entity (defaults to the name)' : 'Company'}
          required={isBroker}
          value={company}
          onChange={setCompany}
        />
        <div className="grid grid-cols-2 gap-2">
          <Field label="Email" value={email} onChange={setEmail} type="email" />
          <Field label="Phone" value={phone} onChange={setPhone} type="tel" />
        </div>
        <Field label={isSponsor ? 'Entity type' : 'Title'} value={title} onChange={setTitle} />
        <Field label="Notes" value={notes} onChange={setNotes} />
        {!isBroker && !isSponsor ? (
          <label className="flex items-center gap-2 text-[11px] text-stone-400">
            <input
              type="checkbox"
              checked={isEntity}
              onChange={(event) => setIsEntity(event.target.checked)}
              className="h-3 w-3 accent-amber-400"
            />
            An organization, not a person
          </label>
        ) : null}

        {existing ? (
          <div className="rounded border border-amber-400/20 bg-amber-400/[0.06] px-3 py-2 text-[11px] text-amber-100" role="alert">
            <div>
              Already on record
              {existing.detail ? <span className="text-stone-400"> — {existing.detail}</span> : null}
            </div>
            <button
              type="button"
              onClick={() => {
                onOpenExisting?.(existing.id)
                onClose()
              }}
              className="mt-1.5 rounded-md border border-amber-400/25 bg-amber-400/[0.08] px-2.5 py-1 text-[11px] font-medium text-amber-200 hover:bg-amber-400/[0.14]"
            >
              Open {name.trim()}
            </button>
          </div>
        ) : null}
        {error ? <p className="text-[11px] text-red-300" role="alert">{error}</p> : null}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="rounded-md border border-amber-400/10 bg-black/40 px-3 py-1 text-[11px] font-medium text-stone-300 transition-colors hover:text-amber-200">
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSave || save.isPending}
            className="rounded-md border border-amber-400/20 bg-amber-400/[0.08] px-3 py-1 text-[11px] font-medium text-amber-200 transition-colors hover:bg-amber-400/[0.14] disabled:cursor-not-allowed disabled:text-stone-600"
          >
            {save.isPending ? 'Saving…' : 'Save contact'}
          </button>
        </div>
      </form>
    </DialogShell>
  )
}

function Field({
  label,
  value,
  onChange,
  required,
  type = 'text',
  autoFocus,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  required?: boolean
  type?: string
  autoFocus?: boolean
}) {
  return (
    <div>
      <label className="mb-1 block text-[10px] uppercase tracking-[0.12em] text-stone-600">
        {label}
        {required && <span className="ml-0.5 text-amber-400">*</span>}
        <input
          type={type}
          value={value}
          autoFocus={autoFocus}
          onChange={(event) => onChange(event.target.value)}
          className="mt-1 block w-full rounded-md border border-amber-400/10 bg-black/40 px-2.5 py-1.5 text-[12px] normal-case tracking-normal text-stone-200 outline-none placeholder:text-stone-600 focus:border-amber-400/30"
        />
      </label>
    </div>
  )
}
